/// <amd-module name="Wit/WorkItemDataManager/CacheManagers/CacheProviders" />
import { IDisposable } from "VSS/Core/IDisposable";
import { ILegacyPlatformService } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { publishEvent } from "VSS/Platform/Telemetry";
import { traceError, traceWarning } from "VSS/Platform/Trace";
import { WITCommonConstants } from "Wit/Common/Generated/Constants";
import { ICacheData, ICacheProvider } from "Wit/WorkItemDataManager/Contracts";

interface ILegacyPageContext {
    webAccessConfiguration: {
        registryItems: {
            [registryKey: string]: unknown;
        };
    };
}

/**
 * Gets an instance of ICacheProvider depending on the browser support
 */
export async function constructCacheProvider(pageContext: IVssPageContext): Promise<ICacheProvider> {
    const legacyPlatformService: ILegacyPlatformService = pageContext.getService("ILegacyPlatformService");
    const legacyPageContext: any = await legacyPlatformService.getLegacyPageContext();
    if (IndexedDBCacheProvider.isSupported(legacyPageContext)) {
        return new IndexedDBCacheProvider(pageContext);
    }

    traceWarning(pageContext, WITCommonConstants.WorkItemTrackingArea, "constructCacheProvider", "Could not find supported WIT cache provider");

    // No supported cache provider found, fall back to null cache provider so that we'll always retrieve from server
    return new NullCacheProvider();
}

/** Timespan after which items will be removed from the cache */
const maximumAgeOfCachedItemsInDays = 6;

/** Time to wait after initializating the cache provider before we run a job to evict old cache entries */
const waitBeforeEvictionInMs = 3000;

/** WIT metadata version registry setting key   */
const witIndexedDBVersionRegistryKey = "/Service/WorkItemTracking/IndexedDbVersion";

class IndexedDBCacheProvider implements ICacheProvider, IDisposable {
    public static isSupported(legacyPageContext: ILegacyPageContext): boolean {
        try {
            // This seems like the only way to get the registry items. LegacyPlatformService can also be used but
            // it does a lot of extra stuff that isn't required for us
            const registryItems = legacyPageContext.webAccessConfiguration.registryItems;
            let isVersionPresent = false;

            // If version is not present then do not use cache for read/write operations.
            if (registryItems && registryItems.hasOwnProperty(witIndexedDBVersionRegistryKey)) {
                isVersionPresent = true;
            }

            return window.indexedDB != null && isVersionPresent;
        } catch (exception) {
            console.warn("Failed to get indexed db WIT cache: ", exception);
            // If explicitly turned off by the user, accessing window.indexedDB throws:
            // > InvalidStateError: A mutation operation was attempted on a database that did not allow mutations.
            // Regardless, we don't want to throw in this function, just return false.
            return false;
        }
    }

    private _disposed = false;
    private _store: KeyValueStore | null;
    private _openPromise: Promise<void> | null;
    private _evictionTimerId: number;
    private _witIndexedDBRegistryVersion: number | null;
    private _pageContext: IVssPageContext;

    constructor(pageContext: IVssPageContext) {
        this._pageContext = pageContext;

        this._store = new KeyValueStore(pageContext);
        this._witIndexedDBRegistryVersion = null;
        const registryItems = (window as any).__vssPageContext.webAccessConfiguration.registryItems;

        if (registryItems && registryItems.hasOwnProperty(witIndexedDBVersionRegistryKey)) {
            this._witIndexedDBRegistryVersion = Number(registryItems[witIndexedDBVersionRegistryKey]);
        }

        this._openPromise = this._store
            .open("wit", "metaDataCache")
            .then(() => {
                this._openPromise = null;
            })
            .then(() => this._scheduleEviction())
            .catch(error => {
                // Publish error to CI
                traceError(
                    pageContext,
                    WITCommonConstants.WorkItemTrackingArea,
                    "OpenKeyValueStore",
                    `Could not open IndexedDB: ${error?.message || error}`
                );

                // Opening the store failed
                this._store = null;
                this._disposed = true;
            });
    }

    public dispose(): void {
        if (this._disposed) {
            return;
        }

        this._disposed = true;

        if (this._store) {
            this._openPromise = null;
            this._store.close();
            this._store = null;
        }

        if (this._evictionTimerId !== undefined) {
            window.clearTimeout(this._evictionTimerId);
        }
    }

    public store<T>(projectId: string, key: string, data: ICacheData<T>): Promise<void> {
        return this._ensureStore(() => this._store!.set(this._getKey(projectId, key), data));
    }

    public delete(projectId: string, key: string): Promise<void> {
        return this._ensureStore(() => this._store!.delete(this._getKey(projectId, key)));
    }

    public read<T>(projectId: string, key: string): Promise<ICacheData<T> | void> {
        return this._ensureStore<ICacheData<T>>(() => {
            return this._store!.get(this._getKey(projectId, key));
        });
    }

    public update<T>(projectId: string, key: string, data: (data: ICacheData<T>) => ICacheData<T>): Promise<void> {
        return this._ensureStore(() => this._store!.getAndSet(this._getKey(projectId, key), data));
    }

    /**
     * Ensure that the connection to the store is open. If it's not, ignore the operation.
     */
    private _ensureStore<T = void>(operation: () => Promise<T | void>): Promise<T | void> {
        if (!this._store || this._disposed) {
            // Ignore the operation
            return Promise.resolve();
        }

        if (this._openPromise) {
            return this._openPromise.then(() => {
                if (!this._store) {
                    // Store was disposed, ignore this operation
                    return;
                }

                return operation();
            });
        }

        return operation();
    }

    private _getKey(projectId: string, key: string): string {
        return `wit/${this._witIndexedDBRegistryVersion}/${projectId}/${key}`;
    }

    private _scheduleEviction() {
        // Evict old cache entries after TTI in order to not interfere with page loading
        this._evictionTimerId = window.setTimeout(this._evictEntries, waitBeforeEvictionInMs);
    }

    private _evictEntries = () => {
        const maxAgeInMS = maximumAgeOfCachedItemsInDays * 24 * 60 * 60 * 1000;
        const cutoff = Date.now() - maxAgeInMS;

        this._ensureStore(
            // Get all items in the store
            () =>
                this._store!.getAll<ICacheData<Object>>()
                    // Filter to expired items
                    .then(contents => Object.keys(contents).filter(key => contents[key].cachedAt < cutoff))
                    // Serially delete items from the store
                    .then(keysToDelete => {
                        if (keysToDelete.length > 0) {
                            publishEvent(this._pageContext, WITCommonConstants.WorkItemTrackingArea, "Caching", {
                                numberOfEvictedItems: keysToDelete.length
                            });
                        }

                        return keysToDelete.reduce((prev, key) => prev.then(() => this._store!.delete(key)), Promise.resolve(null));
                    })
        );
    };
}

/**
 * IndexedDB backed key value store
 */
class KeyValueStore {
    private _db: IDBDatabase | null;
    private _dbName: string;
    private _storeName: string;
    private _pageContext: IVssPageContext;

    constructor(pageContext: IVssPageContext) {
        this._pageContext = pageContext;
    }

    /**
     * Open a connection for the given database and keyvalue store
     * @param dbName Name of database
     * @param storeName Name of key value store
     */
    public open(dbName: string, storeName: string): Promise<void> {
        this._dbName = dbName;
        this._storeName = storeName;

        // Prevent upgrades for now
        const version = 1;
        const request = window.indexedDB.open(dbName, version);

        return new Promise((resolve, reject) => {
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
            request.onupgradeneeded = event => {
                console.log(`Upgrading from ${event.oldVersion} to ${event.newVersion}`);

                if (event.oldVersion < 1) {
                    try {
                        // Initial creation of database
                        request.result.createObjectStore(storeName);
                    } catch (exception) {
                        console.error(exception);
                    }
                } else {
                    console.error("Upgrade not supported yet");
                }
            };
        }).then(() => {
            this._db = request.result;

            this._sendQuotaInformation();
        });
    }

    /**
     * Close connection to database and keyvalue store
     */
    public close(): Promise<void> {
        return new Promise(resolve => {
            if (this._db) {
                this._db.close();
                this._db = null;
            }
        });
    }

    public getAll<T>(): Promise<{ [key: string]: T }> {
        return new Promise((resolve, reject) => {
            const contents: { [key: string]: T } = {};

            this._usingTransaction<T>("readonly", store => {
                const cursorRequest = store.openCursor();
                cursorRequest.onsuccess = (event: any) => {
                    const cursor: IDBCursorWithValue = event.target.result;
                    if (cursor) {
                        contents[cursor.key as string] = cursor.value;
                        cursor.continue();
                    } else {
                        // No more results
                        resolve(contents);
                    }
                };
            }).then(null, reject);
        });
    }

    public get<T>(key: string): Promise<T | void> {
        return this._usingTransaction<T>("readonly", store => store.get(key));
    }

    public set<T>(key: string, value: T): Promise<void> {
        return this._usingTransaction("readwrite", store => store.put(value, key)).then();
    }

    public getAndSet<T>(key: string, data: (data: T) => T): Promise<void> {
        return this._usingTransaction<void>("readwrite", store => {
            const readRequest = store.get(key);
            readRequest.onsuccess = () => {
                // Read data first
                const cacheData = readRequest.result;

                // Pass to caller
                const dataToCache = data(cacheData);

                // Issue put request
                return store.put(dataToCache, key);
            };
        }).then();
    }

    public delete(key: string): Promise<void> {
        return this._usingTransaction("readwrite", store => store.delete(key)).then();
    }

    private _usingTransaction<T = void>(mode: IDBTransactionMode, operation: (store: IDBObjectStore) => IDBRequest | void): Promise<T | void> {
        return new Promise((resolve, reject) => {
            if (!this._db) {
                reject(new Error("KeyValueStore: DB not ready"));
                return;
            }

            const transaction = this._db.transaction(this._storeName, mode);

            let request: IDBRequest | null = null;

            transaction.oncomplete = ev => {
                resolve(request);
            };

            // When an error occurs or the transaction is aborted we want to reject the returned promise
            const errorHandler = (error: any) => {
                if (error && error.target && error.target.error) {
                    reject(error.target.error);
                } else {
                    reject(error);
                }
            };

            transaction.onabort = errorHandler;
            transaction.onerror = errorHandler;

            const store = transaction.objectStore(this._storeName);
            request = operation(store) || null;

            if (request) {
                // Also reject promise when the store request fails
                request.onerror = e => {
                    reject(e);
                };
            }
        }).then((request: IDBRequest | null) => {
            if (request) {
                return request.result as T;
            }
            return;
        });
    }

    private _sendQuotaInformation() {
        try {
            const untypedNavigator = navigator as any;
            if (untypedNavigator && untypedNavigator.webkitTemporaryStorage && untypedNavigator.webkitTemporaryStorage.queryUsageAndQuota) {
                untypedNavigator.webkitTemporaryStorage.queryUsageAndQuota((usedQuota: number, availableQuota: number) => {
                    publishEvent(this._pageContext, WITCommonConstants.WorkItemTrackingArea, "IndexedDB", {
                        usedQuota,
                        availableQuota
                    });
                });
            }
        } catch (e) {
            // Swallow and continue
        }
    }
}

/**
 * Cache provider which no-ops for every operation
 */
export class NullCacheProvider implements ICacheProvider {
    public delete(projectId: string, key: string): Promise<void> {
        return Promise.resolve();
    }

    public store<T>(projectId: string, key: string, data: ICacheData<T>): Promise<void> {
        return Promise.resolve();
    }

    public update<T>(projectId: string, key: string, data: (data: ICacheData<T>) => ICacheData<T>): Promise<void> {
        return Promise.resolve();
    }

    public read<T>(projectId: string, key: string): Promise<T | undefined> {
        return Promise.resolve(undefined);
    }
}
